import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner, Alert, Row, Col } from "react-bootstrap";
import { FiPlus, FiTrash2, FiTag } from "react-icons/fi";
import { getHost, defaultClientId } from "../utils/utils";

const API_URL = `${getHost()}/api/client/category-defaults`;
const AVAILABLE_CATEGORIES = ["Sneakers", "Alternative", "Ticket", "TCG"];
const getToken = () => localStorage.getItem("jwt");

const CategoryDefaultsModal = ({ show, onHide, clients }) => {
  const [selectedClient, setSelectedClient] = useState("");
  const [defaults, setDefaults] = useState([]);
  const [allowedCategories, setAllowedCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (show) {
      setSelectedClient(defaultClientId(clients));
      setMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show || !selectedClient) return;
    setLoading(true);
    setDefaults([]);
    fetch(`${API_URL}?client_id=${selectedClient}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setDefaults(data.category_defaults || []);
        const server = data.allowed_categories || [];
        setAllowedCategories(
          AVAILABLE_CATEGORIES.filter((c) => server.includes(c)),
        );
        setLoading(false);
      })
      .catch(() => {
        setDefaults([]);
        setAllowedCategories([]);
        setLoading(false);
      });
  }, [show, selectedClient]);

  const addDefault = () => {
    const used = new Set(defaults.map((d) => d.category));
    const nextCat = allowedCategories.find((c) => !used.has(c));
    if (!nextCat) return;
    setDefaults([...defaults, { category: nextCat, webhook_url: "" }]);
  };

  const updateDefault = (index, field, value) => {
    const next = [...defaults];
    next[index] = { ...next[index], [field]: value };
    setDefaults(next);
  };

  const removeDefault = (index) => {
    setDefaults(defaults.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    const cleaned = defaults
      .filter((d) => d.category && d.webhook_url && d.webhook_url.trim())
      .map((d) => ({
        category: d.category,
        webhook_url: d.webhook_url.trim(),
        regions: d.regions || [],
      }));
    try {
      const res = await fetch(API_URL, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ client_id: selectedClient, category_defaults: cleaned }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setDefaults(data.category_defaults || []);
      const c = data.cascade || {};
      setMessage(
        `✅ Saved. Added ${(c.added || []).length}, updated ${(c.updated || []).length}, removed ${(c.removed || []).length}.`,
      );
      setTimeout(() => setMessage(""), 4000);
    } catch {
      setMessage("❌ Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="text-main d-flex align-items-start gap-3">
          <FiTag className="fs-24 flex-shrink-0" />
          <div>
            <div className="fs-18 fw-600 lh-1">Category Defaults</div>
            <div className="fs-14 text-white-45 mt-1">
              One default webhook per category. New monitors added to that category automatically get it (existing monitors are not affected).
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-white modal-white-bg">
        {message && (
          <Alert
            variant={message.startsWith("✅") ? "success" : "danger"}
            className="mb-3"
          >
            {message}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label className="text-main">Select Group/Client</Form.Label>
          <Form.Select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="form-input-field rounded-3 text-main custom-select-white fs-16"
          >
            <option value="">Select a group...</option>
            {clients.map((cl) => (
              <option key={cl._id} value={cl._id}>
                {cl.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {selectedClient && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Form.Label className="text-main mb-0">Defaults</Form.Label>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={addDefault}
                disabled={loading || defaults.length >= allowedCategories.length}
              >
                <FiPlus />
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" variant="light" />
                <p className="text-white-65 mt-2 mb-0">Loading defaults...</p>
              </div>
            ) : defaults.length === 0 ? (
              <div className="text-center py-3 text-white-65">
                No defaults configured. Click + to add one.
              </div>
            ) : (
              defaults.map((entry, i) => (
                <div key={i} className="border rounded p-3 mb-2">
                  <Row className="align-items-center g-2">
                    <Col md={4}>
                      <Form.Select
                        value={entry.category}
                        onChange={(e) => updateDefault(i, "category", e.target.value)}
                        className="form-input-field rounded-3 text-white-65 custom-select-white"
                      >
                        {allowedCategories.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={7}>
                      <Form.Control
                        type="text"
                        value={entry.webhook_url}
                        onChange={(e) => updateDefault(i, "webhook_url", e.target.value)}
                        placeholder="Discord webhook URL"
                        className="form-input-field"
                      />
                    </Col>
                    <Col md={1} className="d-flex justify-content-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeDefault(i)}
                      >
                        <FiTrash2 />
                      </Button>
                    </Col>
                  </Row>
                </div>
              ))
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="btn-secondary rounded-3 py-1 px-3 fw-600 fs-12"
          onClick={onHide}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          className="btn-primary rounded-3 py-1 px-3 fw-600 fs-12"
          onClick={handleSave}
          disabled={saving || !selectedClient || loading}
        >
          {saving ? <Spinner size="sm" /> : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CategoryDefaultsModal;
